class VideoScrollSequence {
  constructor(element) {
    this.container = element;
    this.videoContainer = this.container.querySelector('.video-sequence__video-container');
    this.desktopVideo = this.container.querySelector('.video-sequence__video-desktop');
    this.mobileVideo = this.container.querySelector('.video-sequence__video-mobile');
    this.currentFrameElement = this.container.querySelector('.js-current-frame');
    this.totalFramesElement = this.container.querySelector('.js-total-frames');
    
    // Get the active video based on viewport
    this.activeVideo = window.innerWidth <= 767 ? this.mobileVideo : this.desktopVideo;
    
    // Video state
    this.isVideoLoaded = false;
    this.isVideoReady = false;
    this.totalFrames = 0;
    this.currentFrame = 0;
    this.videoDuration = 0;
    
    // Intersection observer for preloading
    this.intersectionObserver = null;
    this.isIntersecting = false;
    
    // RAF and timing
    this.lastScrollPosition = window.scrollY;
    this.lastFrameTime = performance.now();
    this.frameDelta = 1000 / 60; // Target 60fps
    this.ticking = false;
    
    // Debug flag
    this.debug = false;
    
    // Initialize if we have a video
    if (this.activeVideo) {
      this.initialize();
    } else if (this.debug) {
      console.warn('No video element found for scroll sequence');
    }
  }

  initialize() {
    // Set up video event listeners for both videos
    this.setupVideoEvents(this.desktopVideo);
    this.setupVideoEvents(this.mobileVideo);
    
    // Set up intersection observer for smart loading
    this.setupIntersectionObserver();
    
    // Force load the current device's video
    this.preloadActiveVideo();
    
    // Listen for resize to switch between mobile and desktop videos
    window.addEventListener('resize', this.handleResize.bind(this));
    
    // Bind scroll event
    window.addEventListener('scroll', () => {
      this.lastScrollPosition = window.scrollY;
      
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          if (this.isIntersecting && this.isVideoReady) {
            this.updateVideoFrame();
          }
          this.ticking = false;
        });
        this.ticking = true;
      }
    }, { passive: true });
    
    // Handle visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isVideoReady && this.isIntersecting) {
        this.updateVideoFrame();
      }
    });
    
    // Start animation loop
    this.rafId = requestAnimationFrame(() => this.animate());
    
    // Debug logs
    if (this.debug) {
      console.log('Initialized with active video:', this.activeVideo);
      
      // Log video events to help debug issues
      ['loadstart', 'loadeddata', 'canplay', 'playing', 'error'].forEach(event => {
        this.activeVideo.addEventListener(event, () => {
          console.log(`Video event: ${event}`);
        });
      });
    }
  }
  
  setupVideoEvents(video) {
    if (!video) return;
    
    // Preload video data early
    video.addEventListener('loadedmetadata', () => {
      if (video === this.activeVideo) {
        this.onVideoMetadataLoaded(video);
      }
    });
    
    // Mark video as ready when it can play through
    video.addEventListener('canplaythrough', () => {
      if (video === this.activeVideo) {
        this.onVideoCanPlayThrough(video);
      }
    });
    
    // Mobile-specific event handling
    video.addEventListener('loadeddata', () => {
      if (video === this.activeVideo && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        // For mobile browsers, sometimes canplaythrough doesn't fire reliably
        setTimeout(() => {
          if (!this.isVideoReady) {
            this.onVideoCanPlayThrough(video);
          }
        }, 1000);
      }
    });
    
    // Handle video errors
    video.addEventListener('error', (e) => {
      if (this.debug) {
        console.error('Video error:', e);
        console.error('Video error code:', video.error ? video.error.code : 'unknown');
        console.error('Video src:', video.src);
      }
    });
  }
  
  setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries[0].isIntersecting;
        this.isIntersecting = isIntersecting;
        
        if (isIntersecting) {
          // When element comes into view, preload video
          this.preloadActiveVideo();
        }
      },
      {
        root: null,
        rootMargin: '200px 0px', // Start loading 200px before it comes into view
        threshold: 0
      }
    );
    
    this.intersectionObserver.observe(this.container);
  }
  
  preloadActiveVideo() {
    if (!this.activeVideo || this.isVideoLoaded) return;
    
    // If we haven't already, trigger video loading
    try {
      // Force video loading with multiple approaches for better browser support
      this.activeVideo.load();
      
      // For iOS Safari, sometimes we need to trigger a play/pause
      const playPromise = this.activeVideo.play();
      
      if (playPromise !== undefined) {
        playPromise.then(() => {
          this.activeVideo.pause();
          this.activeVideo.currentTime = 0;
        }).catch(e => {
          // Auto-play was prevented, that's fine
          if (this.debug) {
            console.log('Auto-play prevented, continuing with manual load');
          }
        });
      }
      
      // Mark as loaded to avoid repeated attempts
      this.isVideoLoaded = true;
      
      // Force video to be visible (needed for some mobile browsers)
      // This must happen after loading starts
      setTimeout(() => {
        if (!this.isVideoReady) {
          // Check if video has actually loaded
          if (this.activeVideo.readyState >= 3) {
            this.onVideoCanPlayThrough(this.activeVideo);
          }
        }
      }, 1500);
      
    } catch (e) {
      if (this.debug) {
        console.log('Error during preload:', e);
      }
    }
  }
  
  handleResize() {
    // Check if we need to switch active video
    const isMobile = window.innerWidth <= 767;
    const newActiveVideo = isMobile ? this.mobileVideo : this.desktopVideo;
    
    if (newActiveVideo !== this.activeVideo && newActiveVideo) {
      const oldVideo = this.activeVideo;
      this.activeVideo = newActiveVideo;
      
      // Reset state for the new video
      this.isVideoLoaded = false;
      this.isVideoReady = false;
      
      // Check if new video is already loaded
      if (this.activeVideo.readyState >= 3) {
        this.onVideoMetadataLoaded(this.activeVideo);
        this.onVideoCanPlayThrough(this.activeVideo);
      } else {
        // Trigger loading
        this.preloadActiveVideo();
      }
      
      // Pause old video to save resources
      if (oldVideo) {
        oldVideo.pause();
      }
    }
  }
  
  onVideoMetadataLoaded(video) {
    // Get video duration and calculate total frames
    this.videoDuration = video.duration;
    this.totalFrames = Math.floor(this.videoDuration * 30); // Assume 30fps video
    
    // Update total frames display
    if (this.totalFramesElement) {
      this.totalFramesElement.textContent = this.totalFrames;
    }
    
    this.isVideoLoaded = true;
    
    if (this.debug) {
      console.log('Video metadata loaded:', {
        duration: this.videoDuration,
        totalFrames: this.totalFrames,
        readyState: video.readyState
      });
    }
  }
  
  onVideoCanPlayThrough(video) {
    // Video is ready to play
    this.isVideoReady = true;
    
    // Make video visible
    video.classList.add('is-ready');
    
    // Set initial frame
    if (this.isIntersecting) {
      this.updateVideoFrame();
    }
    
    if (this.debug) {
      console.log('Video can play through:', {
        readyState: video.readyState,
        networkState: video.networkState,
        duration: video.duration
      });
    }
  }
  
  animate() {
    const now = performance.now();
    const elapsed = now - this.lastFrameTime;
    
    if (elapsed > this.frameDelta) {
      this.lastFrameTime = now - (elapsed % this.frameDelta);
      
      // Only update when in view and video is ready
      if (this.isIntersecting && this.isVideoReady) {
        // Only update video frame when scrolling
        if (this.lastScrollPosition !== window.scrollY) {
          this.updateVideoFrame();
        }
      }
    }
    
    this.rafId = requestAnimationFrame(() => this.animate());
  }
  
  updateVideoFrame() {
    if (!this.isVideoReady || !this.activeVideo) return;
    
    const rect = this.container.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const containerHeight = this.container.offsetHeight - windowHeight;
    
    // Calculate scroll progress (0 to 1)
    let progress;
    if (rect.top >= 0) {
      progress = 0;
    } else if (rect.bottom <= windowHeight) {
      progress = 1;
    } else {
      progress = Math.abs(rect.top) / containerHeight;
    }
    
    // Calculate time position in video
    const targetTime = progress * this.videoDuration;
    
    // Only update if there's a significant change to avoid unnecessary seeks
    if (Math.abs(this.activeVideo.currentTime - targetTime) > 0.01) {
      // Seek to the correct position
      this.activeVideo.currentTime = targetTime;
      
      // Calculate current frame number
      this.currentFrame = Math.floor(targetTime * 30); // Assume 30fps
      
      // Update frame counter
      if (this.currentFrameElement) {
        this.currentFrameElement.textContent = this.currentFrame;
      }
    }
  }
  
  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    if (this.desktopVideo) {
      this.desktopVideo.pause();
      this.desktopVideo.removeAttribute('src');
      this.desktopVideo.load();
    }
    
    if (this.mobileVideo) {
      this.mobileVideo.pause();
      this.mobileVideo.removeAttribute('src');
      this.mobileVideo.load();
    }
    
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  }
}

// Initialize on document ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.js-video-sequence').forEach(container => {
    new VideoScrollSequence(container);
  });
});

// Initialize on Shopify section load
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.classList.contains('js-video-sequence')) {
    new VideoScrollSequence(event.target);
  }
});
